import { Dialog } from "@headlessui/react";
import { FormBox, ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import FormController from "../../components/fields/FormController";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { QuestionnaireSchema } from "../../model/Questionnaire";

export type QuestionnaireModalProp = {
  questionnaire?: QuestionnaireForm;
  replyButtonSelected?: string;
  onSubmit: (data: QuestionnaireForm) => void;
};

export interface QuestionnaireForm {
  messageId: string;
  target: string;
  subject: string;
  questionNumber: number;
  title1: string;
  answers1: string[];
  title2: string;
  answers2: string[];
  title3: string;
  answers3: string[];
}

const QuestionnaireModal = forwardRef(({
  open,
  toggle,
  closeOnClickOutside,
  questionnaire,
  replyButtonSelected,
  onSubmit,
}: ModalProp & QuestionnaireModalProp, ref) => {
  const [target, setTarget] = useState<string>(questionnaire?.target ?? "all");
  const [initialValues, setInitialValues] = useState<QuestionnaireForm>(questionnaire || {
    messageId: "",
    target: "全員",
    subject: "",
    questionNumber: 1,
    title1: "",
    answers1: ["", "", "", ""],
    title2: "",
    answers2: ["", "", "", ""],
    title3: "",
    answers3: ["", "", "", ""],
  });

  const { control, handleSubmit, setValue, reset, watch } = useForm<QuestionnaireForm>({
    resolver: yupResolver(QuestionnaireSchema),
    defaultValues: {
      ...questionnaire,
      questionNumber: questionnaire?.questionNumber ?? 1,
    },
  });

  const questionNumber = watch("questionNumber", 1);

  // `questionnaire`が変更されたときにフォームの値をリセットする
  useEffect(() => {
    if (questionnaire) {
      reset(questionnaire);
    }
  }, [questionnaire, reset]);

  // モーダルが開かれるタイミングでフォームの値をリセット
  useEffect(() => {
    if (open && questionnaire) {
      reset(questionnaire);
    }
  }, [open, questionnaire, reset]);

  // `replyButtonSelected`が変更されたときに`target`を更新
  useEffect(() => {
    if (replyButtonSelected !== "予定表の出欠(オンライン)") {
      setTarget("全員");
      setValue("target", "全員");
    }
  }, [replyButtonSelected, setValue]);

  // 質問数が変更されたときに、2, 3の質問を設定
  useEffect(() => {
    if (questionNumber < 3) {
      setValue("title3", questionnaire?.title3 || "");
      setValue("answers3", questionnaire?.answers3 || ["", "", "", ""]);
    }
    if (questionNumber < 2) {
      setValue("title2", questionnaire?.title2 || "");
      setValue("answers2", questionnaire?.answers2 || ["", "", "", ""]);
    }
  }, [questionNumber, setValue, questionnaire]);

  const radioItem: RadioButtonItem[] = [
    { id: "all", title: "全員", value: "全員" },
    { id: "online", title: "オンライン参加のみ", value: "オンライン参加のみ" },
  ];

  const handleChange = (newValue: string) => {
    setTarget(newValue);
    setValue("target", newValue);
  };

  useImperativeHandle(ref, () => ({
    resetForm: () => {
      reset({
        target: "全員",
        subject: "",
        questionNumber: 1,
        title1: "",
        answers1: ["", "", "", ""],
        title2: "",
        answers2: ["", "", "", ""],
        title3: "",
        answers3: ["", "", "", ""],
      });
      setTarget("全員");
    }
  }));

  const handleCancel = () => {
    reset(questionnaire || initialValues);
    toggle();
  };

  const handleSubmitForm = (data: QuestionnaireForm) => {
    if (data.questionNumber < 3) {
      data.title3 = "";
      data.answers3 = ["", "", "", ""];
    }
    if (data.questionNumber < 2) {
      data.title2 = "";
      data.answers2 = ["", "", "", ""];
    }
    onSubmit(data);
  };

  const selectQuestionNumberItems = [
    { id: "1", title: "1" },
    { id: "2", title: "2" },
    { id: "3", title: "3" }
  ];

  return (
    <Modal open={open} toggle={toggle} closeOnClickOutside={closeOnClickOutside}>
      <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full max-w-screen-md">
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <div className="text-center sm:mt-3">
            <div className={"flex items-center"}>
              <Dialog.Title as="h3" className="text-left p-4 text-lg font-medium leading-6 text-gray-900 flex-1">
                アンケート設定
              </Dialog.Title>
            </div>
            <div className="overflow-y-auto px-8 text-left pb-8 h-fit max-h-[calc(100vh_-_10rem)]">
              <FormBox gridcol={"grid-cols-1"} className={"sm:pt-[0px]"}>
                {replyButtonSelected === "予定表の出欠(オンライン)" && (
                  <Controller
                    name="target"
                    control={control}
                    render={({ field }) => (
                      <RadioButton
                        children={undefined} {...field}
                        vertical
                        label={"対象者"}
                        value={{ value: field.value, setValue: field.onChange, onChange: handleChange }}
                        radioButtonItem={radioItem}
                        className={"h-8"}
                        required
                      />
                    )}
                  />
                )}
              </FormBox>
              <FormBox gridcol={"grid-cols-1"}>
                <FormController control={control} name={"subject"} label={"件名"} type={"text"} required />
                <FormBox>
                  <RadioButton
                    children={undefined}
                    name={"questionNumber"}
                    label={"質問数"}
                    value={{ value: questionNumber.toString(), setValue: (value: string) => setValue("questionNumber", parseInt(value)), onChange: (e: any) => setValue("questionNumber", parseInt(e.target.value)) }}
                    radioButtonItem={selectQuestionNumberItems}
                    vertical />
                </FormBox>
                
                {questionNumber >= 1 && (
                  <div className="mt-6">
                    <h4 className="text-lg font-medium">質問１</h4>
                    <FormController control={control} name={"title1"} label={"タイトル"} type={"text"} required />
                    <div className="flex space-x-4 mt-2">
                      {["answer1", "answer2", "answer3", "answer4"].map((answer, index) => (
                        <FormController
                          key={index}
                          control={control}
                          name={`answers1.${index}`}
                          label={index === 0 ? "回答" : "　"}
                          labelclassname={index !== 0 ? "invisible" : ""}
                          type={"text"}
                          required={index === 0}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {questionNumber >= 2 && (
                  <div className="mt-6">
                    <h4 className="text-lg font-medium">質問２</h4>
                    <FormController control={control} name={"title2"} label={"タイトル"} type={"text"} required />
                    <div className="flex space-x-4 mt-2">
                      {["answer1", "answer2", "answer3", "answer4"].map((answer, index) => (
                        <FormController
                          key={index}
                          control={control}
                          name={`answers2.${index}`}
                          label={index === 0 ? "回答" : "　"}
                          labelclassname={index !== 0 ? "invisible" : ""}
                          type={"text"}
                          required={index === 0}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {questionNumber >= 3 && (
                  <div className="mt-6">
                    <h4 className="text-lg font-medium">質問３</h4>
                    <FormController control={control} name={"title3"} label={"タイトル"} type={"text"} required />
                    <div className="flex space-x-4 mt-2">
                      {["answer1", "answer2", "answer3", "answer4"].map((answer, index) => (
                        <FormController
                          key={index}
                          control={control}
                          name={`answers3.${index}`}
                          label={index === 0 ? "回答" : "　"}
                          labelclassname={index !== 0 ? "invisible" : ""}
                          type={"text"}
                          required={index === 0}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </FormBox>
            </div>
          </div>
          <div className="bg-gray-100">
            <div className="flex justify-end">
              <ModalFoterBox>
                <Button
                  className="mx-2 mt-1 w-[132px]"
                  outline
                  onClick={handleCancel}
                  children={"キャンセル"}
                />
                <Button
                  className="mt-1 mr-2 w-[132px]"
                  color="indigo"
                  onClick={handleSubmit(handleSubmitForm)}
                  children={"登録"}
                />
              </ModalFoterBox>
            </div>
          </div>
        </form>
      </Dialog.Panel>
    </Modal>
  );
});

QuestionnaireModal.displayName = "QuestionnaireModal";
export default QuestionnaireModal;